'use client';

import Image from '@/components/Image';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { LeftArrowOutlined, RightArrowOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { Autoplay, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { usePersonalityFitTracking } from '../hooks/usePersonalityFitTracking';

export default function PhotoWall({ swiperPart }: { swiperPart: any }) {
  const t = useTranslations('activity.personalityFitGlasses.photoWall');
  const { navigate } = useAppNavigation();
  const { trackMktClick, trackMktExp } = usePersonalityFitTracking();
  const list: any[] = swiperPart?.swiper_lists || [];
  const banner = swiperPart?.bg_img || swiperPart?.banner || swiperPart?.swiperBanner || '';

  const needNav = (list?.length || 0) > 4;
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Lunbo_Area 整体曝光
  const [areaExpRef, areaInView] = useInView({ threshold: 0.3, triggerOnce: true });

  useEffect(() => {
    if (areaInView) {
      try {
        trackMktExp({ mkt_name: 'Lunbo_Area' });
      } catch {
        /* noop */
      }
    }
  }, [areaInView, trackMktExp]);

  // 简单的视频自动播放/暂停控制
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const videos = root.querySelectorAll<HTMLVideoElement>('.video-slide');
    videos.forEach((v) => v.pause());
    const options: IntersectionObserverInit = { threshold: 0.8 };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target as HTMLVideoElement;
        if (entry.isIntersecting) {
          if (video.paused) video.play().catch(() => {});
        } else if (!video.paused) video.pause();
      });
    }, options);
    videos.forEach((v) => io.observe(v));
    return () => io.disconnect();
  }, [list?.length]);

  if (!Array.isArray(list) || list.length === 0) return null;

  const handleSlideClick = (item: any, index: number) => {
    try {
      trackMktClick({
        mkt_name: `Lunbo_${index}`,
        shop_sku: item?.shop_sku || '',
        business_sku: item?.business_sku || '',
        product_sku: item?.sku || '',
      });
    } catch {
      /* noop */
    }
    if (item?.shop_sku) navigate(`/goods-detail/${item.shop_sku}`);
  };

  // Lunbo_{index} 单项曝光组件
  const LunboSlideItem = ({ item, index }: { item: any; index: number }) => {
    const [slideExpRef, slideInView] = useInView({ threshold: 0.5, triggerOnce: true });

    useEffect(() => {
      if (slideInView) {
        try {
          trackMktExp({
            mkt_name: `Lunbo_${index}`,
            shop_sku: item?.shop_sku || '',
            business_sku: item?.business_sku || '',
            product_sku: item?.sku || '',
          });
        } catch {
          /* noop */
        }
      }
    }, [slideInView, index, item?.shop_sku, item?.business_sku, item?.sku, trackMktExp]);

    return (
      <div ref={slideExpRef}>
        {Number(item?.type) === 1 ? (
          <Image
            src={item?.image}
            alt={t('imgAlt', { index })}
            width={600}
            height={600}
            className="w-full h-auto cursor-pointer"
            onClick={() => handleSlideClick(item, index)}
          />
        ) : (
          <div
            className="video-slide-out relative cursor-pointer"
            onClick={() => handleSlideClick(item, index)}
          >
            <video className="video-slide" loop autoPlay muted preload="auto" playsInline>
              <source src={item?.image} />
            </video>
          </div>
        )}
      </div>
    );
  };

  return (
    <div ref={containerRef} className="personality-photo-wall">
      {banner && (
        <Image
          src={banner}
          alt={t('imgAlt', { index: 0 })}
          width={1440}
          height={360}
          className="w-full h-auto"
        />
      )}

      <div ref={areaExpRef} className="relative pb-[7vw] ml-[68px]">
        <Swiper
          modules={[Navigation, Autoplay]}
          slidesPerView={needNav ? 3.6 : 3.5}
          spaceBetween={68}
          loop={needNav}
          autoplay={{ delay: 3000, disableOnInteraction: false, pauseOnMouseEnter: true }}
          navigation={needNav ? { nextEl: '.photo-next', prevEl: '.photo-prev' } : undefined}
          observer
          observeParents
          observeSlideChildren
          className="photo-swiper"
        >
          {list.map((item: any, index: number) => (
            <SwiperSlide key={`${item?.image || 'slide'}-${index}`}>
              <LunboSlideItem item={item} index={index} />
            </SwiperSlide>
          ))}
        </Swiper>

        {needNav && (
          <>
            <div className="photo-prev absolute bottom-0 right-[8.8541666667vw] flex items-center justify-center w-[40px] h-[40px] rounded-full bg-white text-primary-black cursor-pointer z-10">
              <LeftArrowOutlined className="text-[22px]" />
            </div>
            <div className="photo-next absolute bottom-0 right-[5.2083333333vw] flex items-center justify-center w-[40px] h-[40px] rounded-full bg-white text-primary-black cursor-pointer z-10">
              <RightArrowOutlined className="text-[22px]" />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
